import { Dispatch, Middleware, MiddlewareAPI, Action } from 'redux'
import { MESSAGE_TYPES } from 'types/globalMessaging'
import { showMessage } from 'utils/actions/globalMessaging'
import { ILoad, ILoadsState, ILoadDetailsState } from 'apps/fleet-planner/types'
import { fetchLoadCounts } from 'apps/fleet-planner/utils/actions/loadCounts'
import {
  UPDATE_LOAD_STATUS_ACTION_TYPES,
  DELETE_LOAD_ACTION_TYPES,
  UPDATE_LOAD_ACTION_TYPES,
  UPLOAD_ATTACHMENT_ACTION_TYPES,
  DELETE_ATTACHMENT_ACTION_TYPES,
  UPDATE_LOAD_DRIVERS_ACTION_TYPES,
  UPDATE_LOAD_TRAILER_ACTION_TYPES,
  UPDATE_LOAD_ASSIGNMENT_ACTION_TYPES,
} from 'apps/fleet-planner/utils/actions/loadDetails'
import {
  CREATE_LOAD_ACTION_TYPES,
  fetchLoads,
  setLastCreatedLoad,
} from 'apps/fleet-planner/utils/actions/loads'
import {
  shouldPromptDriverUpdates,
  shouldPromptShareLoads,
  getDriversData,
} from 'apps/fleet-planner/utils/driverLedStatus'
import { openDriverOptInModal, showShareLoadToast } from '../actions/modals'

interface LoadDetailsAction extends Action<string> {
  payload: {
    shipment: ILoad
  }
}

// Note: Only refetching on driver/trailer updates because other fields aren't visible in the Loads table
const { UPDATE_LOAD_DRIVERS_SUCCESS } = UPDATE_LOAD_DRIVERS_ACTION_TYPES
const { UPDATE_LOAD_TRAILER_SUCCESS } = UPDATE_LOAD_TRAILER_ACTION_TYPES
const { UPDATE_LOAD_ASSIGNMENT_SUCCESS } = UPDATE_LOAD_ASSIGNMENT_ACTION_TYPES

export const loads: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (
  action: LoadDetailsAction
) => {
  if (
    [
      UPDATE_LOAD_ACTION_TYPES.UPDATE_LOAD_SUCCESS,
      DELETE_LOAD_ACTION_TYPES.DELETE_LOAD_SUCCESS,
      UPDATE_LOAD_DRIVERS_SUCCESS,
      UPDATE_LOAD_TRAILER_SUCCESS,
      UPDATE_LOAD_ASSIGNMENT_SUCCESS,
    ].includes(action.type)
  ) {
    const {
      loads: { isFleet, params },
    }: { loads: ILoadsState } = store.getState()
    store.dispatch(fetchLoads({ ...params }, isFleet))
  }

  if (action.type === UPDATE_LOAD_STATUS_ACTION_TYPES.UPDATE_LOAD_STATUS_SUCCESS) {
    const {
      loads: { params },
    }: { loads: ILoadsState } = store.getState()
    const { filters } = params
    store.dispatch(fetchLoadCounts(filters))
  }

  if (action.type === UPDATE_LOAD_DRIVERS_SUCCESS) {
    const { drivers, id: shipmentId, shipment } = getDriversData(action)
    if (shouldPromptShareLoads(shipment))
      store.dispatch(showShareLoadToast({ shipmentId, message: 'Driver Assigned.' }))
    if (shouldPromptDriverUpdates(shipment)) store.dispatch(openDriverOptInModal(drivers))
  }

  if (action.type === CREATE_LOAD_ACTION_TYPES.CREATE_LOAD_SUCCESS) {
    const {
      loads: { isFleet, params },
    }: { loads: ILoadsState } = store.getState()
    const { drivers, id: shipmentId, shipment } = getDriversData(action)
    if (shouldPromptDriverUpdates(shipment)) store.dispatch(openDriverOptInModal(drivers))
    if (shouldPromptShareLoads(shipment))
      store.dispatch(showShareLoadToast({ shipmentId, message: 'Load created.' }))
    if (isFleet) {
      const load = (action as LoadDetailsAction).payload?.shipment
      if (load) {
        store.dispatch(setLastCreatedLoad(load))
      }
    } else {
      store.dispatch(fetchLoads({ ...params }))
    }

    store.dispatch(
      showMessage('Load added.', {
        type: MESSAGE_TYPES.info,
        typeLabel: '',
        closeDelay: 8000,
        cta: {
          label: 'View Load',
          url: `${__ROOT__}/fms/load/${action.payload.shipment.id}`,
        },
      })
    )
  }
  return next(action)
}

export const loadDetails: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (
  action: LoadDetailsAction
) => {
  if (action.type === UPDATE_LOAD_STATUS_ACTION_TYPES.UPDATE_LOAD_STATUS_SUCCESS) {
    const { drivers, shipment } = getDriversData(action)
    if (shouldPromptDriverUpdates(shipment)) store.dispatch(openDriverOptInModal(drivers))
    store.dispatch(
      showMessage('Status updated', {
        type: MESSAGE_TYPES.info,
        typeLabel: '',
        closeDelay: 3000,
      })
    )
  }
  if (action.type === DELETE_ATTACHMENT_ACTION_TYPES.DELETE_ATTACHMENT_SUCCESS) {
    store.dispatch(
      showMessage('File deleted.', {
        type: MESSAGE_TYPES.info,
        typeLabel: '',
        closeDelay: 3000,
      })
    )
  }
  if (action.type === UPLOAD_ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_SUCCESS) {
    store.dispatch(
      showMessage('File added.', {
        type: MESSAGE_TYPES.info,
        typeLabel: '',
        closeDelay: 3000,
      })
    )
  }
  // Need to find an equivalent event here for failure
  if (action.type === UPLOAD_ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_FAILURE) {
    store.dispatch(
      showMessage('File upload failed', {
        type: MESSAGE_TYPES.alert,
        typeLabel: '',
        closeDelay: 3000,
      })
    )
  }
  if (action.type === DELETE_LOAD_ACTION_TYPES.DELETE_LOAD_SUCCESS) {
    const {
      loadDetails: { isFleet },
    }: { loadDetails: ILoadDetailsState } = store.getState()
    const redirectUrl = isFleet
      ? `${__ROOT__}/fleet-planner/fleet/`
      : `${__ROOT__}/fleet-planner/loads/`
    store.dispatch(
      showMessage('Load Deleted', {
        type: MESSAGE_TYPES.info,
        typeLabel: '',
        closeDelay: 10000,
        cta: {
          label: 'View all loads',
          url: redirectUrl,
        },
      })
    )
  }
  return next(action)
}
